// Operational Engineering — Driller's Schedule.
//
// Method-pluggable: each well-control procedure registers its own
// step-down algorithm. Methods receive a normalised `params` object and
// return an array of plain step rows. The orchestrator
// `computeDrillersSchedule(...)` normalises inputs, dispatches to the
// chosen method, applies operational rounding, and stamps each row with
// the method id so the print layer can render method-specific badges.
//
// Pure — no I/O, no React, no throws. Returns [] (with a `notes` array
// on the result) whenever inputs are insufficient.

import { asNum, PRESSURE_GRADIENT_PPG_PSI_PER_FT } from "../units";

// v1.1 — Surface → Bit → Shoe → Surface display correction (P0). The method
// builders below are UNCHANGED (still end their step-down at strokesToBit /
// FCP); the orchestrator now optionally extends that same output with
// constant-FCP rows through the already-computed strokesBitToShoe /
// strokesShoeToSurface segments. No pressure/stroke/time formula changed —
// see `_extendConstantFcp` below, which reuses the exact stroke/SPM
// arithmetic every row in this file has always used.
export const DRILLERS_SCHEDULE_VERSION = "openg-schedule/1.1";

// Default stroke increment between rows (user-overridable per sheet).
export const DEFAULT_INTERVAL_STROKES = 100;

// Operational rounding — readability beats mathematical precision on
// the rig floor.
const _roundPsi    = (v) => Number.isFinite(v) ? Math.round(v) : null;
const _roundStk    = (v) => Number.isFinite(v) ? Math.round(v) : null;
const _roundMinute = (v) => Number.isFinite(v) ? Math.round(v * 10) / 10 : null;

// ---------------------------------------------------------------------------
// Method registry — extend by adding a builder. Each builder receives:
//   { icpPsi, fcpPsi, strokesToBit, spm, intervalStrokes, kmwPpg, mwInPpg,
//     tvdFt, safetyMarginPpg }
// and returns: [{ stroke, pressurePsi, timeMin, phase, label }]
// `phase` is a free-form short tag for the print column ("kill", "circulate",
// "post-kill"). `label` is an optional human-readable cell hint.
// ---------------------------------------------------------------------------
export const METHOD_REGISTRY = {
  "wait-and-weight":  _waitAndWeight,
  "drillers-method":  _drillersMethod,
  // "engineers-method": future,
  // "volumetric":       future,
  // "bullheading":      future,
  // "other":            future company-specific procedures (caller-supplied)
};

/**
 * Top-level dispatcher. Always returns:
 *   { ok, method, rows, notes, intervalStrokes, totalStrokesToBit,
 *     icpPsi, fcpPsi, version }
 *
 * On insufficient inputs the function returns `ok:false`, `rows:[]`, and a
 * non-empty `notes` array describing what was missing — the editor surfaces
 * those notes inline so the user knows what to fill in.
 */
export function computeDrillersSchedule(params = {}) {
  const method = params.method && METHOD_REGISTRY[params.method]
    ? params.method
    : "wait-and-weight";

  const icpPsi       = asNum(params.icpPsi);
  const fcpPsi       = asNum(params.fcpPsi);
  const strokesToBit = asNum(params.strokesToBit);
  const spm          = asNum(params.spm);
  const intervalStrokes = _resolveInterval(params.intervalStrokes);
  const kmwPpg       = asNum(params.kmwPpg);
  const mwInPpg      = asNum(params.mwInPpg);
  const tvdFt        = asNum(params.tvdFt);
  const safetyMarginPpg = asNum(params.safetyMarginPpg, 0);

  const notes = [];
  if (!Number.isFinite(icpPsi))       notes.push("ICP is missing — enter SIDPP + slow-pump pressure.");
  if (!Number.isFinite(fcpPsi))       notes.push("FCP is missing — confirm KMW + MW_in + slow-pump pressure.");
  if (!Number.isFinite(strokesToBit) || strokesToBit <= 0)
    notes.push("Strokes surface→bit unavailable — enter string internal volume + pump output.");
  if (!Number.isFinite(spm) || spm <= 0)
    notes.push("Pump rate (SPM) missing — required for the time column.");
  if (notes.length > 0) {
    return {
      ok: false, method, rows: [], notes,
      intervalStrokes, totalStrokesToBit: Number.isFinite(strokesToBit) ? strokesToBit : null,
      icpPsi:  _roundPsi(icpPsi),
      fcpPsi:  _roundPsi(fcpPsi),
      version: DRILLERS_SCHEDULE_VERSION,
    };
  }

  const builder = METHOD_REGISTRY[method];
  const raw = builder({
    icpPsi, fcpPsi, strokesToBit, spm, intervalStrokes,
    kmwPpg, mwInPpg, tvdFt, safetyMarginPpg,
  });

  // Surface → Bit → Shoe → Surface (P0 display correction) — extend the
  // method's own output (which ends at FCP, bit on bottom) with
  // constant-FCP rows through the already-computed strokesBitToShoe /
  // strokesShoeToSurface segments. Optional: a well/kill sheet that hasn't
  // filled in bit→shoe / shoe→surface volumes yet simply keeps the
  // schedule stopping at the bit, exactly as before this extension existed.
  // Never recomputes ICP/FCP/strokesToBit — those are the same values
  // already validated above.
  const strokesBitToShoe     = asNum(params.strokesBitToShoe);
  const strokesShoeToSurface = asNum(params.strokesShoeToSurface);
  let extended = raw;
  let totalStrokesToShoe = null;
  let totalStrokesToSurface = null;
  if (Number.isFinite(strokesBitToShoe) && strokesBitToShoe > 0) {
    extended = extended.concat(_extendConstantFcp(strokesToBit, strokesBitToShoe, {
      fcpPsi, spm, intervalStrokes, phase: "bit-to-shoe",
      finalLabel: "Kill mud reaches shoe — FCP held",
    }));
    totalStrokesToShoe = strokesToBit + strokesBitToShoe;
    if (Number.isFinite(strokesShoeToSurface) && strokesShoeToSurface > 0) {
      extended = extended.concat(_extendConstantFcp(totalStrokesToShoe, strokesShoeToSurface, {
        fcpPsi, spm, intervalStrokes, phase: "shoe-to-surface",
        finalLabel: "Kill mud reaches surface — FCP held",
      }));
      totalStrokesToSurface = totalStrokesToShoe + strokesShoeToSurface;
    }
  }

  const rows = extended.map((r) => ({
    stroke:       _roundStk(r.stroke),
    pressurePsi:  _roundPsi(r.pressurePsi),
    timeMin:      _roundMinute(r.timeMin),
    phase:        r.phase || "kill",
    label:        r.label || "",
  }));

  return {
    ok: true,
    method,
    rows,
    notes,
    intervalStrokes,
    totalStrokesToBit: strokesToBit,
    // Milestone cumulative totals for the print/graph layer to anchor on —
    // plain sums of the already-computed segment values above, never a new
    // engineering output. Null when the segment wasn't supplied upstream.
    totalStrokesToShoe:    _roundStk(totalStrokesToShoe),
    totalStrokesToSurface: _roundStk(totalStrokesToSurface),
    icpPsi: _roundPsi(icpPsi),
    fcpPsi: _roundPsi(fcpPsi),
    version: DRILLERS_SCHEDULE_VERSION,
  };
}

// ---------------------------------------------------------------------------
// Method implementations
// ---------------------------------------------------------------------------

// Wait & Weight — single circulation. Pressure ramps linearly from ICP at
// stroke 0 to FCP at strokesToBit. Step rows every `intervalStrokes`
// strokes, with a final row pinned at exactly `strokesToBit`.
function _waitAndWeight({ icpPsi, fcpPsi, strokesToBit, spm, intervalStrokes }) {
  const rows = [];
  const slope = (fcpPsi - icpPsi) / strokesToBit;       // psi per stroke

  for (let s = 0; s < strokesToBit; s += intervalStrokes) {
    rows.push({
      stroke: s,
      pressurePsi: icpPsi + slope * s,
      timeMin: s / spm,
      phase: "kill",
      label: s === 0 ? "Start — ICP" : "",
    });
  }
  // Always anchor the final row at exactly strokesToBit / FCP — operational
  // priority: the bit-on-bottom pressure must be exact even if the prior row
  // is one increment short.
  rows.push({
    stroke: strokesToBit,
    pressurePsi: fcpPsi,
    timeMin: strokesToBit / spm,
    phase: "kill",
    label: "Bit on bottom — FCP",
  });
  return rows;
}

// Driller's Method — two circulations.
//   Phase 1 ("circulate"): pump SCR (slow-pump pressure) holding standpipe
//                          at ICP until annulus is clear of kick fluids.
//                          We emit a single anchor row at stroke=0 with
//                          pressurePsi = ICP (no step-down).
//   Phase 2 ("kill"):      identical to Wait & Weight — linear ICP → FCP.
//
// Strokes in Phase 1 are bottom→surface (operationally distinct), but the
// foundation only has surface→bit. For 4.1b we keep Phase 1 as a single
// "hold ICP" anchor and defer multi-stroke Phase 1 detail to a future pass.
function _drillersMethod(params) {
  const phase1 = [{
    stroke: 0,
    pressurePsi: params.icpPsi,
    timeMin: 0,
    phase: "circulate",
    label: "Phase 1 — Hold ICP, circulate kick out",
  }];
  const phase2 = _waitAndWeight(params).map((r, i) => ({
    ...r,
    phase: "kill",
    label: i === 0 ? "Phase 2 — Begin kill mud, ICP" : r.label,
  }));
  return [...phase1, ...phase2];
}

// Constant-FCP extension — used by the orchestrator to continue a method's
// step-down PAST the bit (where every method's own builder already stops)
// through an already-computed downstream segment (bit→shoe, shoe→surface).
// Pressure is pinned at the SAME fcpPsi passed in (no new pressure formula —
// FCP already holds from the moment kill mud reaches the bit). Stroke/time
// rows use the identical `stroke`, `stroke / spm` arithmetic every builder
// above already uses. Always anchors a final row at exactly
// `startStroke + phaseLength` — same "exact anchor" convention as
// _waitAndWeight's own strokesToBit row.
function _extendConstantFcp(startStroke, phaseLength, { fcpPsi, spm, intervalStrokes, phase, finalLabel }) {
  const rows = [];
  for (let s = intervalStrokes; s < phaseLength; s += intervalStrokes) {
    rows.push({
      stroke: startStroke + s,
      pressurePsi: fcpPsi,
      timeMin: (startStroke + s) / spm,
      phase,
      label: "",
    });
  }
  rows.push({
    stroke: startStroke + phaseLength,
    pressurePsi: fcpPsi,
    timeMin: (startStroke + phaseLength) / spm,
    phase,
    label: finalLabel,
  });
  return rows;
}

// ---------------------------------------------------------------------------
function _resolveInterval(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) return DEFAULT_INTERVAL_STROKES;
  return n;
}

// Re-export the gradient constant so callers can echo it in the trace
// without dragging in lib/openg/units explicitly.
export const SCHEDULE_PRESSURE_GRADIENT = PRESSURE_GRADIENT_PPG_PSI_PER_FT;
